/**
 * Slide-out cart drawer (TNK-105 phase 3). Triggered from the header cart
 * button; lists the cards currently in the shop CartContext and exposes a
 * "Proceed to reserve" CTA that drops the buyer into the existing reserve
 * flow.
 *
 * Reads inventory from ShopInventoryContext (TNK-106 phase 2) so opening
 * the drawer is instant — the layout-level fetch has usually already
 * resolved by the time the buyer adds anything to the cart.
 *
 * Backdrop fade + right-slide via framer-motion (TNK-106 phase 1).
 */

import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowRight,
  Package,
  ShoppingCart,
  Trash2,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

import { imageUrl } from "@/features/imageUpload";
import { storefrontPrice } from "@/features/storefront";
import { useStorefrontCardLookup } from "@/features/useStorefrontCardLookup";
import { useStorefrontBundleLookup } from "@/features/useStorefrontBundleLookup";

import { useShopCart } from "@/routes/shop";

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function CartDrawer({ open, onClose }: CartDrawerProps) {
  const cart = useShopCart();
  // TNK-124 phase G: same lookup hook the cart page uses — warmed
  // first page + single-card fallback for ids beyond it.
  const lookup = useStorefrontCardLookup(cart.ids);
  // TNK-212 phase 2b — resolve cart bundles the same way.
  const bundleLookup = useStorefrontBundleLookup(cart.productIds);

  // Lock body scroll while the drawer is open so the underlying page can't
  // ride along when a buyer scrolls the cart list.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const items = lookup.cards;
  const bundles = bundleLookup.bundles;
  const itemCount = items.length + bundles.length;
  const total =
    items.reduce((s, c) => s + storefrontPrice(c), 0) +
    bundles.reduce((s, b) => s + b.price, 0);
  const loading =
    lookup.phase === "loading" || bundleLookup.phase === "loading";
  const errored = lookup.phase === "error" || bundleLookup.phase === "error";

  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-50 flex"
          role="dialog"
          aria-modal="true"
          aria-label="Cart"
        >
          {/* Backdrop fades */}
          <motion.div
            className="absolute inset-0 bg-black/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Panel slides in from the right */}
          <motion.div
            className="relative ml-auto flex h-full w-full max-w-md flex-col overflow-hidden"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            style={{
              background: "var(--color-card)",
              borderLeft: "1px solid var(--color-border)",
            }}
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-4">
              <div className="flex items-center gap-2">
                <ShoppingCart size={16} className="text-primary" />
                <h2 className="shop-display text-base font-bold text-foreground">
                  Your Cart
                </h2>
                {cart.count > 0 && (
                  <span
                    className="shop-mono rounded-sm px-1.5 py-0.5 text-[10px]"
                    style={{
                      background: "rgba(201,168,76,0.2)",
                      color: "var(--color-primary)",
                    }}
                  >
                    {cart.count}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close cart"
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {cart.count === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 px-5 text-center">
                  <p className="text-sm text-muted-foreground">
                    Your cart is empty.
                  </p>
                  <Link
                    to="/shop/inventory"
                    onClick={onClose}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  >
                    Browse cards
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              ) : loading ? (
                <p className="px-5 py-12 text-center text-sm text-muted-foreground">
                  Loading…
                </p>
              ) : errored ? (
                <p className="px-5 py-12 text-center text-sm text-destructive">
                  Couldn’t load the inventory.
                </p>
              ) : (
                <>
                  <ul className="divide-y divide-border">
                    {/* TNK-212 phase 2b — bundle rows first, then singles. */}
                    {bundles.map((b) => {
                      const img = b.coverRef
                        ? imageUrl(b.coverRef, "thumb")
                        : null;
                      return (
                        <li key={b.id} className="flex items-center gap-3 p-4">
                          <Link
                            to="/shop/bundle/$id"
                            params={{ id: b.id }}
                            onClick={onClose}
                            className="flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-muted text-muted-foreground"
                          >
                            {img ? (
                              <img
                                src={img}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Package size={16} />
                            )}
                          </Link>
                          <div className="min-w-0 flex-1">
                            <Link
                              to="/shop/bundle/$id"
                              params={{ id: b.id }}
                              onClick={onClose}
                              className="block truncate text-sm font-medium text-foreground hover:underline"
                            >
                              {b.name}
                            </Link>
                            <div className="truncate text-[11px] text-muted-foreground">
                              Bundle · {b.cardCount} card
                              {b.cardCount === 1 ? "" : "s"}
                            </div>
                            <div
                              className="shop-mono mt-1 text-sm font-bold"
                              style={{ color: "var(--color-primary)" }}
                            >
                              ${b.price.toFixed(2)}
                            </div>
                          </div>
                          <button
                            type="button"
                            aria-label={`Remove ${b.name}`}
                            onClick={() => cart.removeProduct(b.id)}
                            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </li>
                      );
                    })}
                    {items.map((card) => {
                      // TNK-125: thumb — drawer row at small scale.
                      const img = card.imageFrontRef
                        ? imageUrl(card.imageFrontRef, "thumb")
                        : null;
                      return (
                        <li
                          key={card.id}
                          className="flex items-center gap-3 p-4"
                        >
                          <Link
                            to="/shop/card/$id"
                            params={{ id: card.id }}
                            onClick={onClose}
                            className="h-16 w-12 shrink-0 overflow-hidden rounded bg-muted"
                          >
                            {img && (
                              <img
                                src={img}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            )}
                          </Link>
                          <div className="min-w-0 flex-1">
                            <Link
                              to="/shop/card/$id"
                              params={{ id: card.id }}
                              onClick={onClose}
                              className="block truncate text-sm font-medium text-foreground hover:underline"
                            >
                              {card.name}
                            </Link>
                            <div className="truncate text-[11px] text-muted-foreground">
                              {card.setName} · {card.condition}
                            </div>
                            <div
                              className="shop-mono mt-1 text-sm font-bold"
                              style={{ color: "var(--color-primary)" }}
                            >
                              ${storefrontPrice(card).toFixed(2)}
                            </div>
                          </div>
                          <button
                            type="button"
                            aria-label={`Remove ${card.name}`}
                            onClick={() => cart.remove(card.id)}
                            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {/* Unavailable-since-added callout — surfaces the
                      stale ids the lookup hook couldn't resolve, with
                      a one-click bulk remove so the buyer can clean
                      the cart before reserving. */}
                  {lookup.missing.size > 0 && (
                    <div className="mx-5 my-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/[0.04] p-3">
                      <AlertCircle
                        size={14}
                        className="mt-0.5 shrink-0 text-amber-500"
                      />
                      <div className="flex-1">
                        <p className="text-[12px] font-semibold text-foreground">
                          {lookup.missing.size} card
                          {lookup.missing.size === 1 ? "" : "s"} no longer
                          available
                        </p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">
                          Sold or removed since you added them.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          for (const id of lookup.missing) cart.remove(id);
                        }}
                        className="shrink-0 rounded-md border border-amber-500/40 px-2.5 py-1 text-[11px] font-semibold text-amber-500 hover:bg-amber-500/10"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer with subtotal + CTA — only when there are items */}
            {itemCount > 0 && (
              <div className="shrink-0 border-t border-border px-5 py-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {itemCount} item{itemCount !== 1 ? "s" : ""}
                  </span>
                  <span
                    className="shop-mono text-xl font-bold"
                    style={{ color: "var(--color-primary)" }}
                  >
                    ${total.toFixed(2)}
                  </span>
                </div>
                <Link
                  to="/shop/reserve"
                  onClick={onClose}
                  className="flex w-full items-center justify-center gap-2 rounded-md py-3 text-sm font-bold transition-opacity hover:opacity-90"
                  style={{
                    background: "var(--color-primary)",
                    color: "var(--color-primary-foreground)",
                  }}
                >
                  Proceed to reserve
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <p className="mt-2 text-center text-[10px] text-muted-foreground">
                  Reservations are requests — the seller confirms availability.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
